import { type StepCaptions, StepLabelParamsByCode } from '@ralph/contracts'
import type { TraceNode } from '@/hooks/use-run-stream.ts'
import type { Messages } from '@/i18n/index.ts'

/**
 * A step's caption, in the reader's language (PRD-27 D-8, LOC-042).
 *
 * Both registers live in one module on purpose. They must key off exactly the same code and
 * exactly the same parameters — and say different things with them. The trace panel is the
 * operator's instrument and names the model profile the deployment chose; the conversation
 * column may not, because a badge describing how the machine was configured invites a reader
 * to argue with it instead of reading the answer (AGM-022). Keeping the two lookups apart in
 * two files is how they would drift into keying off different things, which is the failure
 * this product's register rule exists to prevent.
 *
 * The catalogue is a parameter rather than an import, as every pure module here takes it: a
 * capture at module scope freezes the locale at import and then renders the previous language
 * with no type error, no runtime error and no failing test (F-3, LOC-012, D-25).
 */

/**
 * The shared half: does this node name a caption this build can render, with the values that
 * caption needs?
 *
 * The parameters are parsed through the CONTRACT's own schema rather than trusted. The wire
 * field is a loose record on purpose — a server one deploy ahead must not break this client —
 * so "the code is known" and "the payload is the one that code takes" are two different
 * questions, and a `model_call` that arrived without its alias has to degrade to the stored
 * English rather than render "Reasoning with undefined" at somebody.
 */
const renderCaption = (captions: StepCaptions, node: TraceNode): string | null => {
  const code = node.labelCode
  // `Object.hasOwn` rather than `in`, because the code arrives as an arbitrary string off the
  // wire: `in` also finds `toString` and `constructor` on the prototype, and this build would
  // then call one of them as though it were a schema.
  if (!code || !Object.hasOwn(StepLabelParamsByCode, code)) return null

  const known = code as keyof typeof StepLabelParamsByCode
  const params = StepLabelParamsByCode[known].safeParse(node.labelParams ?? {})
  if (!params.success) return null

  const caption = (captions[known] as (values: unknown) => string)(params.data)
  return caption.length > 0 ? caption : null
}

/**
 * The operator's wording, for the trace lane.
 *
 * Falls back to the server's own prose, which is the whole reason that prose is still on the
 * wire and still in the row: a step recorded before this release has no code, and one
 * recorded by a server that has learned a code this build has not is the same case.
 */
export const traceStepCaption = (t: Messages['trace'], node: TraceNode): string =>
  renderCaption(t.stepLabel, node) ?? node.label

/**
 * The reader's wording, for the conversation column.
 *
 * `null` rather than the stored `label`, and that difference is the point: the English prose
 * says "Reasoning with balanced", and putting the model profile in front of a reader is
 * precisely what this column does not do (AGM-022). A caller with no caption to show says
 * something generic of its own instead — the trace panel, one column to the right, has the
 * operator's line in full.
 */
export const chatStepCaption = (t: Messages['chat'], node: TraceNode | undefined): string | null =>
  node ? renderCaption(t.stepActivity, node) : null
